import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { CONTACTS } from "@/data/apartments";
import { track } from "@/lib/gtag";
import { useLang } from "@/i18n";

export const scrollToId = (href) => {
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -70, duration: 1.4 });
  else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

const LangSwitch = ({ dark }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      className={`flex items-center overflow-hidden rounded-full border transition-colors duration-500 ${dark ? "border-ink/30" : "border-ivory/50"}`}
      data-testid="lang-switch"
    >
      {["it", "fr", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-switch-${l}`}
          className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors duration-300 ${
            lang === l ? "bg-terracotta text-ivory" : dark ? "text-ink/60 hover:text-ink" : "text-ivory/75 hover:text-ivory"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const LINKS = [
    { label: t.nav.progetto, href: "#progetto" },
    { label: t.nav.appartamenti, href: "#appartamenti" },
    { label: t.nav.galleria, href: "#galleria" },
    { label: t.nav.contesto, href: "#contesto" },
    { label: t.nav.contatti, href: "#contatti" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  const textColor = scrolled || open ? "text-ink" : "text-ivory";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled || open ? "border-b border-line/60 bg-ivory/85 backdrop-blur-xl" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-[4.5rem] max-w-[1440px] items-center justify-between px-6 sm:px-10">
        <a
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            setOpen(false);
            window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.4 }) : window.scrollTo(0, 0);
          }}
          className={`transition-colors duration-500 ${textColor}`}
          data-testid="header-logo"
        >
          <Logo markClass="h-10 w-auto" />
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className={`text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors duration-300 hover:text-terracotta ${textColor}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <LangSwitch dark={scrolled || open} />
          <a
            href="#contatti"
            onClick={(e) => go(e, "#contatti")}
            data-testid="header-cta"
            className="hidden rounded-full bg-terracotta px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-ivory shadow-md transition-all duration-300 hover:scale-105 hover:bg-sun hover:text-ink sm:block"
          >
            {t.headerCta}
          </a>
          <button
            className={`transition-colors duration-500 lg:hidden ${textColor}`}
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-button"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" strokeWidth={1.25} /> : <Menu className="h-6 w-6" strokeWidth={1.25} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="border-b border-line bg-ivory px-6 pb-8 pt-2 lg:hidden"
            data-testid="mobile-menu"
          >
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                className="block border-b border-line/60 py-4 font-serif text-2xl text-ink transition-colors hover:text-terracotta"
              >
                {l.label}
              </a>
            ))}
            <div className="mt-6 flex flex-col gap-3">
              <a
                href="#contatti"
                onClick={(e) => go(e, "#contatti")}
                data-testid="mobile-menu-cta"
                className="rounded-full bg-terracotta px-6 py-4 text-center text-[12px] font-bold uppercase tracking-[0.2em] text-ivory shadow-md"
              >
                {t.headerCta}
              </a>
              <a
                href={`tel:${CONTACTS.telefono.replace(/ /g, "")}`}
                onClick={() => track("click_telefono")}
                data-testid="mobile-menu-phone"
                className="flex items-center justify-center gap-2.5 rounded-full border border-ink/25 px-6 py-4 text-[12px] font-bold uppercase tracking-[0.16em] text-ink"
              >
                <Phone className="h-4 w-4" strokeWidth={1.5} />
                {CONTACTS.telefono}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
